import React, { useEffect } from 'react';
import { object, string } from 'yup';
import { Form, Row, Col, Container } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { createTheme, ThemeProvider, Button, SvgIcon, FormGroup } from '@mui/material';
import SyringeIcon from '../../../icons/SyringeBlue.svg?react';
import LightBulbIcon from '../../../icons/LightBulb.svg?react';
import { CheckboxInput } from '../../Helpers/Inputs';
import './MedicalHistory.scss';

const medicalHistoryTheme = {
  components: {
    MuiFormControl: {
      styleOverrides: {
        root: {
          marginBottom: 4,
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          alignItems: "flex-start",
          marginTop: 0,
          marginRight: 0,
          marginBottom: 0,
          marginLeft: -2,
          paddingRight: 10,
        },
        label: {
          paddingTop: 4,
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: "#c6c6c6",
          marginRight: 8,
          padding: 0,
        },
      },
    },
  },
};

export default function MedicalHistory(props) {

  const formMethods = useForm({
    resolver: yupResolver(MedicalHistory.ValidationSchema),
    mode: 'all',
    reValidateMode: 'all',
    defaultValues: props.formState
  });

  const { handleSubmit } = formMethods;

  const noneOfTheAboveDisabled =
    props.formState.mildAsthma ||
    props.formState.severeAsthma ||
    props.formState.mildAnxietyDepression ||
    props.formState.severeAnxietyDepression ||
    props.formState.bipolar ||
    props.formState.cardiovascularheartDisease ||
    props.formState.cancer ||
    props.formState.chronsUlcerativeColitis ||
    props.formState.copd ||
    props.formState.diabetes ||
    props.formState.hivaids ||
    props.formState.highCholesterol ||
    props.formState.kidneyDisease ||
    props.formState.hypertensionOrHighBloodPressure ||
    props.formState.rheumatoidArthritis ||
    props.formState.sleepApnea ||
    props.formState.strokeTia;
  const handleCheckBoxValue = (field, value) => {
    if (field === 'none' && !value) {
      if (!noneOfTheAboveDisabled) {
        return false;
      }
    }
    props.onFieldChange(field, value);
  };

  // uncheck "none" if it becomes disabled
  useEffect(() => {
    if (!noneOfTheAboveDisabled && !props.formState.none) {
      props.onFieldChange('none', true);
    }else if (noneOfTheAboveDisabled && props.formState.none) {
      props.onFieldChange('none', false);
    }
  }, [noneOfTheAboveDisabled]);

  return (
    <ThemeProvider theme={(theme) => createTheme({
      ...theme,
      ...medicalHistoryTheme,
    })}>
      <Container className="medical-history-form personalize-step">
        <FormProvider {...formMethods}>
        <Form onSubmit={handleSubmit(props.onNext)}>
          <Row className="justify-content-center">
            <Col lg={5} className="medical-history-container">
              <Row className="justify-content-center pt-2">
                <div className={`icon-container background-color-16`}>
                  <SvgIcon component={SyringeIcon} color='primary' fontSize="large" />
                </div>

              </Row>
              <Row className="justify-content-center mainTitle">
                <h4>Medical History</h4>
              </Row>
              <Row>
                <p className="titleBox">
                  <span className={`insideTitle primary-color`}>
                    <SvgIcon component={LightBulbIcon} color='primary' />
                    Insider Tip:{' '}
                  </span>
                  <span className="insideText"> By answering a few more questions we can personalize your quote to ensure you get the most accurate price.</span>
                </p>
              </Row>
              <Row>
                <h5 className="subTitle">I have been diagnosed with or treated for:</h5>
              </Row>
              <Row>
                <FormGroup className="checkboxes px-0">
                  <CheckboxInput
                    className="checkBoxInput"
                    name="mildAsthma"
                    id="mildAsthma"
                    checked={props.formState.mildAsthma}
                    onChange={(e) => handleCheckBoxValue('mildAsthma', e.target.checked)}
                    label="Asthma - Mild/Seasonal"
                  />
                  <CheckboxInput
                    className="checkBoxInput"
                    name="severeAsthma"
                    id="severeAsthma"
                    checked={props.formState.severeAsthma}
                    onChange={(e) => handleCheckBoxValue('severeAsthma', e.target.checked)}
                    label="Asthma - Moderate/Severe "
                  />
                  <CheckboxInput
                    className="checkBoxInput"
                    name="mildAnxietyDepression"
                    id="mildAnxietyDepression"
                    checked={props.formState.mildAnxietyDepression}
                    onChange={(e) => handleCheckBoxValue('mildAnxietyDepression', e.target.checked)}
                    label="Anxiety/Depression-Mild/Moderate/Situational"
                  />
                  <CheckboxInput
                    className="checkBoxInput"
                    name="severeAnxietyDepression"
                    id="severeAnxietyDepression"
                    checked={props.formState.severeAnxietyDepression}
                    onChange={(e) => handleCheckBoxValue('severeAnxietyDepression', e.target.checked)}
                    label="Anxiety/Depression- Severe"
                  />
                  <CheckboxInput
                    className="checkBoxInput"
                    name="bipolar"
                    id="bipolar"
                    checked={props.formState.bipolar}
                    onChange={(e) => handleCheckBoxValue('bipolar', e.target.checked)}
                    label="Bipolar"
                  />
                  <CheckboxInput
                    className="checkBoxInput"
                    name="cardiovascularheartDisease"
                    id="cardiovascularheartDisease"
                    checked={props.formState.cardiovascularheartDisease}
                    onChange={(e) => handleCheckBoxValue('cardiovascularheartDisease', e.target.checked)}
                    label="Cardiovascular/Heart Disease"
                  />
                  <CheckboxInput
                    className="checkBoxInput"
                    name="cancer"
                    id="cancer"
                    checked={props.formState.cancer}
                    onChange={(e) => handleCheckBoxValue('cancer', e.target.checked)}
                    label="Cancer (non-basal cell)"
                  />
                  <CheckboxInput
                    className="checkBoxInput"
                    name="chronsUlcerativeColitis"
                    id="chronsUlcerativeColitis"
                    checked={props.formState.chronsUlcerativeColitis}
                    onChange={(e) => handleCheckBoxValue('chronsUlcerativeColitis', e.target.checked)}
                    label="Crohn's/Ulcerative Colitis"
                  />
                  <CheckboxInput
                    className="checkBoxInput"
                    name="copd"
                    id="copd"
                    checked={props.formState.copd}
                    onChange={(e) => handleCheckBoxValue('copd', e.target.checked)}
                    label="COPD"
                  />
                  <CheckboxInput
                    className="checkBoxInput"
                    name="diabetes"
                    id="diabetes"
                    checked={props.formState.diabetes}
                    onChange={(e) => handleCheckBoxValue('diabetes', e.target.checked)}
                    label="Diabetes (Type 1, Type 2, or Pre-Diabetes)"
                  />
                  <CheckboxInput
                    className="checkBoxInput"
                    name="hivaids"
                    id="hivaids"
                    checked={props.formState.hivaids}
                    onChange={(e) => handleCheckBoxValue('hivaids', e.target.checked)}
                    label="HIV/AIDS"
                  />
                  <CheckboxInput
                    className="checkBoxInput"
                    name="highCholesterol"
                    id="highCholesterol"
                    checked={props.formState.highCholesterol}
                    onChange={(e) => handleCheckBoxValue('highCholesterol', e.target.checked)}
                    label="High Cholesterol"
                  />
                  <CheckboxInput
                    className="checkBoxInput"
                    name="hypertensionOrHighBloodPressure"
                    id="hypertensionOrHighBloodPressure"
                    checked={props.formState.hypertensionOrHighBloodPressure}
                    onChange={(e) => handleCheckBoxValue('hypertensionOrHighBloodPressure', e.target.checked)}
                    label="Hypertension or High Blood Pressure"
                  />
                  <CheckboxInput
                    className="checkBoxInput"
                    name="kidneyDisease"
                    id="kidneyDisease"
                    checked={props.formState.kidneyDisease}
                    onChange={(e) => handleCheckBoxValue('kidneyDisease', e.target.checked)}
                    label="Kidney Disease"
                  />
                  <CheckboxInput
                    className="checkBoxInput"
                    name="rheumatoidArthritis"
                    id="rheumatoidArthritis"
                    checked={props.formState.rheumatoidArthritis}
                    onChange={(e) => handleCheckBoxValue('rheumatoidArthritis', e.target.checked)}
                    label="Rheumatoid Arthritis"
                  />
                  <CheckboxInput
                    className="checkBoxInput"
                    name="sleepApnea"
                    id="sleepApnea"
                    checked={props.formState.sleepApnea}
                    onChange={(e) => handleCheckBoxValue('sleepApnea', e.target.checked)}
                    label="Sleep Apnea"
                  />
                  <CheckboxInput
                    className="checkBoxInput"
                    name="strokeTia"
                    id="strokeTia"
                    checked={props.formState.strokeTia}
                    onChange={(e) => handleCheckBoxValue('strokeTia', e.target.checked)}
                    label="Stroke/TIA"
                  />
                  <CheckboxInput
                    className="checkBoxInput"
                    name="none"
                    id="none"
                    checked={props.formState.none}
                    onChange={(e) => handleCheckBoxValue('none', e.target.checked)}
                    label="None of the above"
                    disabled={noneOfTheAboveDisabled}
                  />
                </FormGroup>
              </Row>
            </Col>
          </Row>
          {/* Buttons */}
          <Row className="pre-quote-nav-control">
            <Col className="text-start btn-back">
              <Button variant="outlined" size="medium" color="primary" className="px-4" onClick={props.onBack}>
                BACK
              </Button>
            </Col>
            <Col className="text-end">
              <Button variant="contained" size="medium" color="primary" type="submit" className="px-4">
                NEXT
              </Button>
            </Col>
          </Row>
        </Form>
        </FormProvider>
      </Container>
    </ThemeProvider>
  );
}

function checkBoxValidation(
  mildAsthma,
  severeAsthma,
  mildAnxietyDepression,
  severeAnxietyDepression,
  bipolar,
  cardiovascularheartDisease,
  cancer,
  chronsUlcerativeColitis,
  copd,
  diabetes,
  hivaids,
  highCholesterol,
  hypertensionOrHighBloodPressure,
  kidneyDisease,
  rheumatoidArthritis,
  sleepApnea,
  strokeTia
) {
  if (
    mildAsthma === false &&
    severeAsthma === false &&
    mildAnxietyDepression === false &&
    severeAnxietyDepression === false &&
    bipolar === false &&
    cardiovascularheartDisease === false &&
    cancer === false &&
    chronsUlcerativeColitis === false &&
    copd === false &&
    diabetes === false &&
    hivaids === false &&
    highCholesterol === false &&
    hypertensionOrHighBloodPressure === false &&
    kidneyDisease === false &&
    rheumatoidArthritis === false &&
    sleepApnea === false &&
    strokeTia === false
  )
    return true;
  else return false;
}

MedicalHistory.ValidationSchema = object().shape({
    none: string()
    .required()
    .when(
      [
        'mildAsthma',
        'severeAsthma',
        'mildAnxietyDepression',
        'severeAnxietyDepression',
        'bipolar',
        'cardiovascularheartDisease',
        'cancer',
        'chronsUlcerativeColitis',
        'copd',
        'diabetes',
        'hivaids',
        'highCholesterol',
        'hypertensionOrHighBloodPressure',
        'kidneyDisease',
        'rheumatoidArthritis',
        'sleepApnea',
        'strokeTia',
      ],
      (values, schema) => {
        const hasCheckedBox = checkBoxValidation(values);
        if (hasCheckedBox) {
          return schema.matches(/^true/, 'Please check one checkbox.');
        }
        return schema.required('This field is required.')
      }
    ),
});

MedicalHistory.DefaultFormState = (prefill = false) => {
  return {
    mildAsthma: false,
    severeAsthma: false,
    mildAnxietyDepression: false,
    severeAnxietyDepression: false,
    bipolar: false,
    cardiovascularheartDisease: false,
    cancer: false,
    chronsUlcerativeColitis: false,
    copd: false,
    diabetes: false,
    hivaids: false,
    highCholesterol: false,
    hypertensionOrHighBloodPressure: false,
    kidneyDisease: false,
    rheumatoidArthritis: false,
    sleepApnea: false,
    strokeTia: false,
    none: true,
  };
};
